<template>
  <div id="login">
    <div class="login-block">
      <div class="main-dialog">
        <BForm @submit.prevent="onClickLogin">
          <div class="modal-title">
            {{ t("__appName") }}
          </div>
          <div
            v-if="error"
            class="modal-error-bar text-center"
          >
            {{ error }}
          </div>
          <div class="input-wrapper">
            <BFormGroup
              label-cols="2"
              :label="t('__userId')"
              label-for="user-id"
              label-class="label"
            >
              <BFormInput
                id="user-id"
                v-model="form.username"
                type="text"
                required
                :placeholder="t('__userIdPlaceholder')"
              />
            </BFormGroup>
          </div>
          <div class="input-wrapper">
            <BFormGroup
              label-cols="2"
              :label="t('__userPassword')"
              label-for="user-password"
              label-class="label"
            >
              <BFormInput
                id="user-password"
                v-model="form.password"
                type="password"
                required
                :placeholder="t('__userPasswordPlaceholder')"
              />
            </BFormGroup>
          </div>
          <div class="input-wrapper mb-40px">
            <BFormGroup
              label-cols="2"
              label-for="project-dropdown"
              label-class="label"
              :label="t('__project')"
            >
              <BDropdown
                id="project-dropdown"
                :text="dropdownLabel"
                size="sm"
                class="project-dropdown"
              >
                <BDropdownItem
                  v-for="project in projectOptions"
                  :key="project"
                  @click="onProjectChange(project)"
                >
                  {{ project }}
                </BDropdownItem>
              </BDropdown>
            </BFormGroup>
          </div>
          <BButton
            variant="primary"
            class="w-100"
            :class="{ disabled: loading }"
            :disabled="loading"
            type="submit"
          >
            {{ t("__login") }}
          </BButton>
        </BForm>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, computed } from 'vue';
import { useConfigStore } from '@/store/config';
import { useRouter } from 'vue-router';
import { BForm, BFormGroup, BFormInput, BDropdown, BDropdownItem, BButton } from 'bootstrap-vue-next';
import { useI18n } from 'vue-i18n';
import { useApi, useConstants, useCookie } from '@/composables/useAppGlobals';

const store = useConfigStore();
const router = useRouter();
const $api = useApi();
const $const = useConstants();
const $cookie = useCookie();
const { t } = useI18n();

const form = reactive({ username: '', password: '' });
const error = ref('');
const loading = ref(false);
const projectOptions = computed(() => Object.values($const.PROJECT_KEY_NAME_MAPPING || {}));
const selectedProject = ref<string | null>(null);
const dropdownLabel = computed(() => selectedProject.value || t('__selectProject'));

async function onClickLogin() {
  error.value = '';
  loading.value = true;
  if (selectedProject.value == null) {
    loading.value = false;
    error.value = t('__selectProject');
    return;
  }
  try {
    const projectKey = selectedProject.value ? $const.PROJECT_NAME_KEY_MAPPING[selectedProject.value] : undefined;
    const data = await $api.userLogin({
      username: form.username.trim().toLowerCase(),
      password: form.password,
      project_name: projectKey,
    });
    if (data?.users) {
      store.setUsers(data.users || []);
    }
    $cookie.setCookie(
      $const.COOKIES.KEY.USER_TOKEN,
      data.token,
      $const.COOKIES.CONFIG.EXPIRES_1M,
    );
    $cookie.setCookie(
      $const.COOKIES.KEY.USER_ID,
      data.id,
      $const.COOKIES.CONFIG.EXPIRES_1M,
    );
    $cookie.setCookie(
      $const.COOKIES.KEY.USER_NAME,
      data.name,
      $const.COOKIES.CONFIG.EXPIRES_1M,
    );
    $cookie.setCookie(
      $const.COOKIES.KEY.USER_ROLE,
      data.role || 'user',
      $const.COOKIES.CONFIG.EXPIRES_1M,
    );
    $cookie.setCookie(
      $const.COOKIES.KEY.USER_IS_RESET,
      String(data.is_reset),
      $const.COOKIES.CONFIG.EXPIRES_1M,
    );
    if (data.project_id) {
      $cookie.setCookie(
        $const.COOKIES.KEY.PROJECT_ID,
        data.project_id,
        $const.COOKIES.CONFIG.EXPIRES_1M,
      );
    }
    if (projectKey) {
      $cookie.setCookie(
        $const.COOKIES.KEY.PROJECT_NAME,
        projectKey,
        $const.COOKIES.CONFIG.EXPIRES_1M,
      );
    }
    store.setIsLogin(true);
    loading.value = false;

    // Check if user needs to reset password
    if (data.is_reset) {
      $cookie.setCookie(
        $const.COOKIES.KEY.USER_IS_RESET,
        'true',
        $const.COOKIES.CONFIG.EXPIRES_1M,
      );
      router.push($const.ROUTES.PASSWORD_RESET);
    } else {
      router.push($const.ROUTES.DASHBOARD);
    }
  } catch {
    loading.value = false;
    error.value = t('__errorLogin');
  }
}

function onProjectChange(project: string) {
  selectedProject.value = project;
  $cookie.setCookie(
    'project',
    $const.PROJECT_NAME_KEY_MAPPING[project],
    $const.COOKIES.CONFIG.EXPIRES_1M,
  );
}

// lifecycle
store.setIsBusy(false);
</script>

<style lang="scss" scoped>
@use "@/assets/scss/common/alignment" as *;

.modal-title {
  font-size: 28px;
  font-weight: bold;
  line-height: 40px;
  text-align: center;
  color: #212121;
  letter-spacing: 0.2em;
  margin-bottom: 20px;
}

.modal-error-bar {
  background-color: rgba(234, 71, 91, 0.2);
  margin-bottom: 24px;
  font-size: 14px;
  font-weight: 500;
  font-stretch: normal;
  line-height: 36px;
  letter-spacing: normal;
  color: #ea475b;
}

#login {
  position: relative;
  height: 100vh;  
  background-color: #e6e9ef;
}

.login-block {
  @extend .center-v-absolute;
  left: 0;
  right: 0;
  margin: auto;
  width: 480px;
}

.logo {
  @extend .center-h-absolute;
  bottom: 60px;
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.main-dialog {
  position: relative;
  width: 100%;
  animation-name: fade-in;
  animation-duration: 600ms;
  background-color: white;
  border-radius: 4px;
  box-shadow: 0px 4px 14px rgba(40, 75, 178, 0.1);
  padding: 40px 30px;
}

:deep(.label) {
  text-align: right;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.43;
  letter-spacing: normal;
  color: #303030;
}

.input-wrapper {
  margin-bottom: 24px;
}

.forgot-pwd-text {
  margin-top: 14px;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  color: #4d8edc;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}
.project-dropdown {
  display: block;
  width: 100%;
  max-width: 348px;
  height: 38px;
  .caret {
    margin-left: auto;
  }
}

@media (max-width: 480px) {
  .login-block { width: 92%; padding: 20px; }
  .project-dropdown { max-width: 100%; }
}

:deep(.project-dropdown .dropdown-toggle) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  color: #212121;
  font-size: 1rem;
  padding: 0.375rem 0.75rem;
  height: 100%;
  width: 100%;
}

:deep(.project-dropdown .dropdown-toggle:focus) {
  outline: 0;
}

#login :deep(.project-dropdown .dropdown-toggle:active),
#login :deep(.project-dropdown .dropdown-toggle.active),
#login :deep(.project-dropdown.show .dropdown-toggle) {
  background-color: #fff;
  border-color: #ced4da;
  color: #212121;
}

:deep(.project-dropdown .dropdown-toggle::after) {
  margin-left: auto;   /* pushes caret to the far right */
}

/* Minimize dropdown animations */
:deep(.project-dropdown .dropdown-menu) {
  transition: none;
  animation: none;
}

:deep(.project-dropdown .dropdown-item) {
  transition: none;
  color: #212121;
}

:deep(.project-dropdown .dropdown-item:hover),
:deep(.project-dropdown .dropdown-item:focus),
:deep(.project-dropdown .dropdown-item:active) {
  transition: none;
}
</style>
